// SPDX-License-Identifier: Apache-2.0

import type { Extension, Organization } from '@medplum/fhirtypes';
import type { NotificationKey } from './registry';
import { ALL_NOTIFICATIONS, getNotificationDef } from './registry';
import type { NotificationTypeDefinition } from './types';

// ─── Extension URL constants ────────────────────────────────────────────────

export const DISABLED_NOTIFICATIONS_SYSTEM = 'http://polarissleep.com/disabled-notifications';
export const DISABLED_NOTIFICATION_KEY_URL = `${DISABLED_NOTIFICATIONS_SYSTEM}/key`;

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Extract the set of disabled notification keys from a tenant Organization.
 *
 * Reads the parent extension by URL and collects child sub-extensions whose
 * url === DISABLED_NOTIFICATION_KEY_URL and whose valueString is a string.
 * Returns an empty set if the Organization is undefined or the extension is absent.
 */
export function getDisabledNotificationKeys(org: Organization | undefined): Set<string> {
  const parent = org?.extension?.find((e) => e.url === DISABLED_NOTIFICATIONS_SYSTEM);
  const keys = (parent?.extension ?? [])
    .filter((e) => e.url === DISABLED_NOTIFICATION_KEY_URL && typeof e.valueString === 'string')
    .map((e) => e.valueString as string);
  return new Set(keys);
}

/**
 * Build the FHIR extension structure for disabled notification keys.
 *
 * Defensively filters out any key where the corresponding notification
 * definition is marked essential, so an essential type can never be
 * persisted as disabled even if passed in.
 */
export function buildDisabledNotificationsExtension(keys: string[]): Extension {
  const safe = keys.filter((k) => {
    const def = (ALL_NOTIFICATIONS as Record<string, NotificationTypeDefinition | undefined>)[k];
    return !def?.essential;
  });
  return {
    url: DISABLED_NOTIFICATIONS_SYSTEM,
    extension: safe.map((k) => ({ url: DISABLED_NOTIFICATION_KEY_URL, valueString: k })),
  };
}

/**
 * Determine if a notification type is disabled for a tenant.
 *
 * Returns false for essential types regardless of the disabled set,
 * enforcing the essential lock at the gate. Otherwise, returns whether
 * the key is present in the disabled set.
 */
export function isNotificationTypeDisabled(key: NotificationKey, disabled: Set<string>): boolean {
  if (getNotificationDef(key).essential) {
    return false; // essential locked on
  }
  return disabled.has(key);
}
